import { useCallback, useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { TableRow } from '../../template/Table';
import { classifyFile, delimitedToRows } from './tableImport';
import { normalizeTable } from './tableModel';

interface ExtractService {
  extractTableFromFile: (
    modelType: string,
    modelId: string,
    file: File,
  ) => Promise<{ rows: unknown[] }>;
}

/**
 * Import a table from a dropped/pasted file. CSV/TSV is parsed in the browser;
 * spreadsheets, documents and images go to `contentManagement/table/extract`
 * (images/PDFs are transcribed by the vision model there). The result is always
 * run through `normalizeTable` before it reaches the block.
 */
export function useTableImport(
  backendService: BackendService | undefined,
  modelId: string,
  onRows: (rows: TableRow[]) => void,
  modelType: string = 'FundOpportunity',
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (task: () => Promise<TableRow[]>) => {
      setBusy(true);
      setError(null);
      try {
        const rows = await task();
        if (rows.length === 0) {
          setError('No table was found in that file.');
          return;
        }
        onRows(rows);
      } catch (err) {
        setError(
          (err as { message?: string })?.message ??
            'Could not read that file — try a CSV, or paste the table directly.',
        );
      } finally {
        setBusy(false);
      }
    },
    [onRows],
  );

  const importFile = useCallback(
    (file: File) => {
      if (classifyFile(file) === 'delimited') {
        run(async () => delimitedToRows(await file.text()));
        return;
      }
      if (!backendService) {
        setError('Importing this file type needs a backend connection.');
        return;
      }
      run(async () => {
        const result = await (backendService as unknown as ExtractService).extractTableFromFile(
          modelType,
          modelId,
          file,
        );
        return normalizeTable((result.rows ?? []) as TableRow[]);
      });
    },
    [backendService, modelId, modelType, run],
  );

  const importImage = useCallback(
    (blob: Blob) => {
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'pasted-image.png', { type: blob.type || 'image/png' });
      importFile(file);
    },
    [importFile],
  );

  return { busy, error, importFile, importImage, clearError: () => setError(null) };
}
